import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import {
  APP_ROLES,
  ROLE_PERMISSIONS,
  PERMISSION_CATALOG,
  SUPER_ADMIN_EXTRA_PERMISSIONS,
  type PermissionName,
} from "@/lib/rbac";
import { Badge } from "@/components/ui/badge";
import { Shield, Lock, Crown, Check, Minus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AccessDenied } from "@/components/auth/AccessDenied";

export const Route = createFileRoute("/_authenticated/admin/roles")({
  component: RolesAdminPage,
});

// Canonical enterprise roles (match the backend AppRoles set exactly).
const ROLES = APP_ROLES;

// SuperAdmin is deliberately NOT one of the 4 AppRole values — it folds into SystemAdmin for the
// permission-union system (see normalizeRole in rbac.ts), because a SuperAdmin holds every
// SystemAdmin permission by role composition on the backend. It gets its own display-only row here
// so this page still shows what it adds on top, without disturbing hasPermission()/hasAnyRole()
// anywhere else in the app.
const SUPER_ADMIN_PERMISSIONS: PermissionName[] = [
  ...ROLE_PERMISSIONS.SystemAdmin,
  ...SUPER_ADMIN_EXTRA_PERMISSIONS,
];

function RolesAdminPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission("manage:roles")) {
    return <AccessDenied message="You don't have permission to view roles." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Roles & Permissions</h1>
        <p className="text-sm text-muted-foreground">
          Capabilities are defined in code and enforced by the backend per role. This view is
          read-only.
        </p>
      </div>

      {/* Per-role capability cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
        <Card className="border-amber-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              SuperAdmin
              <Crown className="h-4 w-4 text-amber-500" />
            </CardTitle>
            <CardDescription>
              Inherits every SystemAdmin permission, plus {SUPER_ADMIN_EXTRA_PERMISSIONS.length}{" "}
              exclusive to it
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {SUPER_ADMIN_PERMISSIONS.map((p) => (
                <Badge
                  key={p}
                  variant="secondary"
                  className={
                    SUPER_ADMIN_EXTRA_PERMISSIONS.includes(p)
                      ? "text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400"
                      : "text-[10px]"
                  }
                >
                  {p}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {ROLES.map((role) => {
          const perms = ROLE_PERMISSIONS[role] ?? [];
          return (
            <Card key={role}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  {role}
                  {role === "SystemAdmin" ? (
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Shield className="h-4 w-4 text-primary" />
                  )}
                </CardTitle>
                <CardDescription>
                  {perms.length} {perms.length === 1 ? "permission" : "permissions"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {perms.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No elevated permissions (baseline access only).
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {perms.map((p: PermissionName) => (
                      <Badge key={p} variant="secondary" className="text-[10px]">
                        {p}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Full capability matrix */}
      <Card>
        <CardHeader>
          <CardTitle>Capability Matrix</CardTitle>
          <CardDescription>Which roles hold each permission.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4 font-medium">Permission</th>
                  <th className="px-2 py-2 text-center font-medium whitespace-nowrap text-amber-600 dark:text-amber-400">
                    SuperAdmin
                  </th>
                  {ROLES.map((r) => (
                    <th key={r} className="px-2 py-2 text-center font-medium whitespace-nowrap">
                      {r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_CATALOG.map(
                  (perm: { name: PermissionName; label: string; description: string }) => (
                    <tr key={perm.name} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        <p className="font-medium">{perm.label}</p>
                        <p className="text-xs text-muted-foreground">{perm.description}</p>
                      </td>
                      <td className="px-2 py-2 text-center">
                        {SUPER_ADMIN_PERMISSIONS.includes(perm.name) ? (
                          <Check className="mx-auto h-4 w-4 text-emerald-600" />
                        ) : (
                          <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />
                        )}
                      </td>
                      {ROLES.map((r) => {
                        const has = (ROLE_PERMISSIONS[r] ?? []).includes(perm.name);
                        return (
                          <td key={r} className="px-2 py-2 text-center">
                            {has ? (
                              <Check className="mx-auto h-4 w-4 text-emerald-600" />
                            ) : (
                              <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
